import React, { useState, useRef, useMemo, useEffect, useCallback, memo } from 'react';
import * as d1Api from '../../services/d1Api';
import * as PlyrModule from 'plyr-react';
const Plyr = PlyrModule.default || Object.values(PlyrModule)[0];
import "plyr/dist/plyr.css";
import { Lock, VideoOff, Maximize, Minimize, Settings2, ChevronDown } from 'lucide-react';

const QUALITY_MAP = {
    '360p': 'small',
    '480p': 'large',
    '720p': 'hd720',
    '1080p': 'hd1080',
};

const TeleportingWatermark = memo(({ phone, containerRef }) => {
    const watermarkRef = useRef(null);

    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 20;
        let cancelled = false;

        const injectWatermark = () => {
            if (cancelled) return;
            const container = containerRef.current?.querySelector('.plyr');
            if (!container) {
                attempts++;
                if (attempts < maxAttempts) setTimeout(injectWatermark, 300);
                return;
            }

            if (watermarkRef.current) {
                try { watermarkRef.current.remove(); } catch (_) {}
            }

            const watermarkDiv = document.createElement('div');
            watermarkDiv.className = 'wm-teleport';
            watermarkDiv.setAttribute('aria-hidden', 'true');
            const span = document.createElement('span');
            span.className = 'wm-text';
            span.textContent = phone.replace(/\s+/g, '');
            watermarkDiv.appendChild(span);
            container.appendChild(watermarkDiv);
            watermarkRef.current = watermarkDiv;
        };

        injectWatermark();

        return () => {
            cancelled = true;
            if (watermarkRef.current) {
                try { watermarkRef.current.remove(); } catch (_) {}
            }
        };
    }, [phone]);

    return null;
});
TeleportingWatermark.displayName = 'TeleportingWatermark';

const YoutubePlayer = memo(({ youtubeId, playerRef, phone, quality, onPlay, onPause, onTimeUpdate }) => {
    const containerRef = useRef(null);

    return (
        <div className="plyr-container relative w-full h-full bg-black flex items-center justify-center" ref={containerRef} style={{ '--plyr-color-main': '#dc2626' }}>
            <div className="w-full h-full">
                <Plyr
                    ref={playerRef}
                    source={{ type: 'video', sources: [{ src: youtubeId, provider: 'youtube' }] }}
                    options={{
                        autoplay: false,
                        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'],
                        settings: ['speed'],
                        youtube: {
                            noCookie: true,
                            rel: 0,
                            showinfo: 0,
                            modestbranding: 1,
                            iv_load_policy: 3,
                            fs: 0,
                            playerVars: { vq: QUALITY_MAP[quality] || 'hd720' }
                        }
                    }}
                    onPlay={onPlay}
                    onPause={onPause}
                    onTimeUpdate={onTimeUpdate}
                />
            </div>
            <TeleportingWatermark phone={phone} containerRef={containerRef} />
        </div>
    );
});
YoutubePlayer.displayName = 'YoutubePlayer';

export const LecturePlayer = ({ lecture, isLoading, studentPhone = "01xxxxxxxxx", isLocked = false, currentVideoPart = 0, setCurrentVideoPart = null }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [quality, setQuality] = useState('720p'); // Default fixed to 720p
    const [showQualityMenu, setShowQualityMenu] = useState(false);
    const [qualityChanging, setQualityChanging] = useState(false);
    const playerRef = useRef(null);
    const wrapperRef = useRef(null);

    const handleQualityChange = useCallback((newQuality) => {
        if (newQuality === quality) {
            setShowQualityMenu(false);
            return;
        }
        setQualityChanging(true);
        setShowQualityMenu(false);
        setQuality(newQuality);
        // Give time for the component to remount and iframe to load
        setTimeout(() => setQualityChanging(false), 1500);
    }, [quality]);

    const toggleFullScreen = async () => {
        try {
            if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                if (wrapperRef.current.requestFullscreen) await wrapperRef.current.requestFullscreen();
                else if (wrapperRef.current.webkitRequestFullscreen) await wrapperRef.current.webkitRequestFullscreen();
            } else {
                if (document.exitFullscreen) await document.exitFullscreen();
                else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
            }
        } catch (err) { console.error("تعذر فتح الشاشة الكاملة:", err); }
    };

    useEffect(() => {
        const handleFsChange = () => {
            setIsFullScreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        document.addEventListener('webkitfullscreenchange', handleFsChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFsChange);
            document.removeEventListener('webkitfullscreenchange', handleFsChange);
        };
    }, []);

    const getYoutubeId = (url) => {
        if (!url || typeof url !== 'string') return null;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)|([a-zA-Z0-9_-]{11})/);
        return (match && (match[1] || match[2])) ? (match[1] || match[2]) : null;
    };

    const parsedVideos = useMemo(() => {
        if (!lecture) return [];
        try {
            const parsed = typeof lecture.videos_list === 'string' ? JSON.parse(lecture.videos_list) : lecture.videos_list;
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch (e) {}
        if (lecture.youtube_url || lecture.youtube_id || lecture.video_url) return [{ title: 'الجزء الرئيسي', url: lecture.youtube_url || lecture.youtube_id || lecture.video_url }];
        return [];
    }, [lecture]);

    const youtubeId = useMemo(() => getYoutubeId(parsedVideos[currentVideoPart]?.url || lecture?.video_url), [parsedVideos, currentVideoPart, lecture]);

    useEffect(() => {
        const preventDev = (e) => {
            if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) || (e.ctrlKey && e.key === 'U')) e.preventDefault();
        };
        const handleContext = (e) => e.preventDefault();
        document.addEventListener('keydown', preventDev);
        document.addEventListener('contextmenu', handleContext);
        return () => { document.removeEventListener('keydown', preventDev); document.removeEventListener('contextmenu', handleContext); };
    }, []);

    const progressSentRef = useRef(false);
    useEffect(() => { progressSentRef.current = false; }, [youtubeId, lecture?.id]);

    const handleTimeUpdate = useCallback((e) => {
        const player = e?.detail?.plyr;
        if (!player || !player.duration || progressSentRef.current) return;
        if ((player.currentTime / player.duration) * 100 >= 80) {
            progressSentRef.current = true;
            const userId = localStorage.getItem('userId');
            if (userId && lecture?.id) d1Api.submitAssignment(userId, lecture.id).catch(() => {});
        }
    }, [lecture?.id]);

    if (isLoading) return <div className="aspect-video bg-black rounded-[24px] flex items-center justify-center animate-pulse border border-white/10"><div className="text-slate-500 font-bold">جاري التحميل...</div></div>;
    if (isLocked) return <div className="aspect-video bg-gray-950 rounded-[24px] flex items-center justify-center border-2 border-brand-red/20"><Lock size={60} className="text-red-500" /></div>;
    if (!youtubeId) return <div className="aspect-video bg-gray-950 rounded-[24px] flex items-center justify-center"><VideoOff size={60} className="text-slate-500" /></div>;

    return (
        <div className="w-full flex flex-col" dir="rtl">
            {parsedVideos.length > 1 && (
                <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm">
                    <div className="flex gap-3 overflow-x-auto custom-scrollbar">
                        {parsedVideos.map((video, idx) => (
                            <button
                                key={idx}
                                onClick={() => { if (setCurrentVideoPart) setCurrentVideoPart(idx); setIsPlaying(false); }}
                                className={`px-5 py-2.5 rounded-full font-bold whitespace-nowrap transition-all duration-200 ${currentVideoPart === idx ? 'bg-red-600 text-white shadow-lg' : 'bg-[#1a1a1a] text-slate-400 hover:bg-[#2a2a2a] hover:text-white'}`}
                            >
                                {video.title || `الجزء ${idx + 1}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div ref={wrapperRef} className="fullscreen-wrapper relative w-full rounded-[24px] bg-black player-glow z-40">
                <button onClick={toggleFullScreen} className="absolute top-4 left-4 z-[200] bg-black/60 hover:bg-brand-red text-white p-2 md:p-3 rounded-xl backdrop-blur-md transition-all flex items-center gap-2 border border-white/10 shadow-lg">
                    {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
                    <span className="hidden md:inline font-bold text-sm">{isFullScreen ? 'تصغير' : 'تكبير الشاشة'}</span>
                </button>

                <div className="absolute top-4 right-4 z-[200] quality-selector-wrapper">
                    <button onClick={() => setShowQualityMenu(!showQualityMenu)} className="quality-toggle-btn bg-black/60 hover:bg-brand-red text-white p-2 md:p-3 rounded-xl backdrop-blur-md transition-all flex items-center gap-1.5 border border-white/10 shadow-lg">
                        <Settings2 size={18} />
                        <span className="font-bold text-sm">{quality}</span>
                        <ChevronDown size={14} className={`transition-transform duration-200 ${showQualityMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showQualityMenu && (
                        <div className="quality-dropdown">
                            {Object.keys(QUALITY_MAP).reverse().map((q) => (
                                <button key={q} onClick={() => handleQualityChange(q)} className={`quality-option ${quality === q ? 'active' : ''}`}>
                                    {q} {quality === q && <span className="quality-check">✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {qualityChanging && (
                    <div className="absolute inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-[24px]">
                        <div className="flex items-center gap-3 bg-black/90 border border-brand-red/50 px-6 py-3 rounded-2xl text-white font-bold">
                            <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-brand-red rounded-full"></div>
                            جاري التبديل لجودة {quality}...
                        </div>
                    </div>
                )}

                {/* The KEY is the magic trick. Changing quality forces React to destroy and rebuild the player with the new VQ param */}
                <YoutubePlayer
                    key={`plyr-${youtubeId}-${currentVideoPart}-${quality}`}
                    youtubeId={youtubeId}
                    playerRef={playerRef}
                    phone={studentPhone}
                    quality={quality}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={handleTimeUpdate}
                />
            </div>

            <style>{`
                .player-glow { box-shadow: 0 0 40px 5px rgba(220, 38, 38, 0.15) !important; border: 1px solid rgba(255,255,255,0.05); }
                .fullscreen-wrapper:fullscreen, .fullscreen-wrapper:-webkit-full-screen { width: 100vw !important; height: 100vh !important; border-radius: 0 !important; display: flex !important; align-items: center; justify-content: center; margin: 0 !important; padding: 0 !important; }
                .fullscreen-wrapper:fullscreen .plyr__video-wrapper, .fullscreen-wrapper:-webkit-full-screen .plyr__video-wrapper { height: 100% !important; width: 100% !important; aspect-ratio: auto !important; }
                .quality-dropdown { position: absolute; top: calc(100% + 8px); right: 0; min-width: 120px; background: rgba(10, 10, 10, 0.92); backdrop-filter: blur(16px); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 14px; padding: 6px; display: flex; flex-direction: column; gap: 2px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6); }
                .quality-option { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-radius: 10px; font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.75); background: transparent; border: none; cursor: pointer; transition: all 0.15s ease; white-space: nowrap; }
                .quality-option:hover { background: rgba(220, 38, 38, 0.25); color: #fff; }
                .quality-option.active { background: rgba(220, 38, 38, 0.35); color: #fff; }
                .quality-check { color: #dc2626; margin-left: 8px; font-weight: 700; }
                @keyframes wmTeleport {
                  0%     { top: 5%; left: 5%; opacity: 0; } 4% { opacity: 1; } 18% { opacity: 1; } 22% { opacity: 0; }
                  22.1%  { top: 85%; left: 75%; opacity: 0; } 26% { opacity: 1; } 40% { opacity: 1; } 44% { opacity: 0; }
                  44.1%  { top: 40%; left: 10%; opacity: 0; } 48% { opacity: 1; } 62% { opacity: 1; } 66% { opacity: 0; }
                  66.1%  { top: 15%; left: 80%; opacity: 0; } 70% { opacity: 1; } 84% { opacity: 1; } 88% { opacity: 0; }
                  88.1%  { top: 75%; left: 20%; opacity: 0; } 92% { opacity: 1; } 98% { opacity: 1; } 100% { opacity: 0; }
                }
                .wm-teleport { position: absolute !important; z-index: 2147483647 !important; pointer-events: none !important; user-select: none !important; animation: wmTeleport 20s infinite !important; display: block !important; }
                .wm-text { display: block; white-space: nowrap; font-size: 18px !important; font-weight: 600 !important; letter-spacing: 2px !important; color: rgba(220, 38, 38, 0.6) !important; text-shadow: 0 0 3px rgba(0, 0, 0, 0.9) !important; line-height: 1; }
                .plyr__video-wrapper iframe { transform: scale(1.17) !important; pointer-events: none !important; }
            `}</style>
        </div>
    );
};